import { jsxs, jsx } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { joinIds, useIsoLayoutEffect, hasContent } from "./internal.js";
const FieldContext = React.createContext(null);
function useFieldControl(props) {
  const field = React.useContext(FieldContext);
  const register = field?.registerControlId;
  const ownId = props.id;
  useIsoLayoutEffect(() => {
    if (!register || ownId === void 0) return void 0;
    register(ownId);
    return () => register(void 0);
  }, [register, ownId]);
  if (!field) return props;
  const merged = {
    id: ownId ?? field.controlId,
    "aria-describedby": joinIds(props["aria-describedby"], field.describedBy),
    "aria-invalid": props["aria-invalid"] ?? (field.invalid ? true : void 0),
    required: props.required ?? field.required,
    disabled: props.disabled ?? field.disabled
  };
  return { ...props, ...merged };
}
function ErrorIcon() {
  return /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 16 16", width: "14", height: "14", "aria-hidden": "true", focusable: "false", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6.25" }),
    /* @__PURE__ */ jsx("path", { d: "M8 4.75v3.75M8 11.1v.05" })
  ] });
}
const Field = React.forwardRef(function Field2({ label, help, error, invalid, required, disabled, hideLabel = false, className, children, ...rest }, ref) {
  const baseId = React.useId();
  const [customControlId, setCustomControlId] = React.useState(void 0);
  const controlId = customControlId ?? `${baseId}-control`;
  const labelId = `${baseId}-label`;
  const helpId = `${baseId}-help`;
  const errorId = `${baseId}-error`;
  const showHelp = hasContent(help);
  const showError = hasContent(error);
  const isInvalid = invalid ?? showError;
  const describedBy = joinIds(showHelp && helpId, showError && errorId);
  const context = React.useMemo(
    () => ({ controlId, labelId, describedBy, invalid: isInvalid, required, disabled, registerControlId: setCustomControlId }),
    [controlId, labelId, describedBy, isInvalid, required, disabled]
  );
  return /* @__PURE__ */ jsxs("div", { ref, className: cx("ev-field", className), "data-invalid": isInvalid ? "true" : void 0, ...rest, children: [
    /* @__PURE__ */ jsxs("label", { id: labelId, htmlFor: controlId, className: cx("ev-field__label", hideLabel && "ev-visually-hidden"), children: [
      label,
      required ? /* @__PURE__ */ jsx("span", { className: "ev-field__required", "aria-hidden": "true", children: "*" }) : null
    ] }),
    /* @__PURE__ */ jsx(FieldContext.Provider, { value: context, children }),
    showHelp ? /* @__PURE__ */ jsx("span", { id: helpId, className: "ev-field__help", children: help }) : null,
    showError ? /* @__PURE__ */ jsxs("span", { id: errorId, className: "ev-field__error", children: [
      /* @__PURE__ */ jsx(ErrorIcon, {}),
      /* @__PURE__ */ jsx("span", { children: error })
    ] }) : null
  ] });
});
Field.displayName = "Field";
export {
  Field,
  useFieldControl
};
//# sourceMappingURL=Field.js.map
